import { useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import {
  chercherDepartements,
  libelleDepartement,
  type Departement,
} from '../utils/departements';

/**
 * Saisie d'un département français, avec suggestions.
 *
 * La liste se propose dès deux caractères — nom ou numéro — et la recherche
 * ignore les accents : « he » comme « 34 » trouvent l'Hérault. Choisir dans
 * la liste enregistre la forme canonique, « Hérault (34) », accents compris :
 * c'est elle qui paraît sur la page Association, et une orthographe
 * approximative saisie à la main y resterait affichée telle quelle.
 *
 * La saisie libre reste possible : le champ ne bloque pas un cas que la
 * liste n'aurait pas prévu.
 */
export default function ChampDepartement({
  id,
  valeur,
  onChange,
  disabled = false,
}: {
  id: string;
  valeur: string;
  onChange: (valeur: string) => void;
  disabled?: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [surligne, setSurligne] = useState(0);
  const conteneur = useRef<HTMLDivElement>(null);

  // Deux caractères avant de proposer : sur une seule lettre, la moitié de
  // la France défile, et la liste cache le champ plus qu'elle ne l'aide.
  const suggestions = valeur.trim().length >= 2 ? chercherDepartements(valeur) : [];
  const listeVisible = ouvert && suggestions.length > 0;

  const choisir = (d: Departement) => {
    onChange(libelleDepartement(d));
    setOuvert(false);
  };

  return (
    <div ref={conteneur} className="relative flex-1">
      <input
        id={id}
        type="text"
        value={valeur}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={listeVisible}
        aria-autocomplete="list"
        placeholder="Tapez deux lettres ou le numéro…"
        className="input w-full"
        onChange={(e) => {
          onChange(e.target.value);
          setOuvert(true);
          setSurligne(0);
        }}
        onFocus={() => setOuvert(true)}
        onBlur={() => {
          // Différé : un clic sur une suggestion passe par le blur du champ,
          // fermer immédiatement avalerait le clic.
          setTimeout(() => setOuvert(false), 150);
        }}
        onKeyDown={(e) => {
          if (!listeVisible) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSurligne((s) => Math.min(s + 1, suggestions.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSurligne((s) => Math.max(s - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            choisir(suggestions[surligne]);
          } else if (e.key === 'Escape') {
            setOuvert(false);
          }
        }}
      />

      {listeVisible && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1"
        >
          {suggestions.map((d, i) => (
            <li key={d.code} role="option" aria-selected={i === surligne}>
              <button
                type="button"
                // mousedown et non click : le blur du champ part avant le
                // click et fermerait la liste sous la souris.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choisir(d);
                }}
                onMouseEnter={() => setSurligne(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                  i === surligne
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <MapPin className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                <span className="flex-1">{d.nom}</span>
                <span className="text-xs text-gray-400 tabular-nums">{d.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
